import { FormOutlined, PlusOutlined } from '@ant-design/icons';
import {
  IssueLiabilityDoc,
  IssueLiabilityResponsibleType,
  IssueProduct,
  issueLiabilityResponsibleTypeMap,
} from '@gooduncles/gu-app-schema';
import { Button, Form, Input, InputNumber, Radio, Select, Space, Tag } from 'antd';
import { FormInstance } from 'antd/es/form';
import { FC, useState } from 'react';

import { calculatePreTaxPrice } from 'src/utils/order-util';

import ResponsibleIdSelector from './ResponsibleIdSelector';
import {
  AmountInputMode,
  LiabilityFormValues,
  costTypeOptions,
  formatAmount,
  formItemStyle,
  labelAlignStyle,
  labelStyle,
  numberInputProps,
  responsibleTypeOptions,
  statusOptions,
} from './liabilityTypes';

interface LiabilityFormProps {
  form: FormInstance<LiabilityFormValues>;
  responsibleType?: IssueLiabilityResponsibleType;
  /** 저장 버튼 클릭 핸들러 (hideActionButtons가 true면 불필요) */
  onSave?: () => void;
  /** 취소 버튼 클릭 핸들러 (hideActionButtons가 true면 불필요) */
  onCancel?: () => void;
  isEditing: boolean;
  editingLiability?: IssueLiabilityDoc | null;
  products?: IssueProduct[] | null;
  onProductSelect?: (product: IssueProduct) => void;
  /** 추가 모드 시 배경색 및 헤더 표시 여부 (기본: true) */
  showHeader?: boolean;
  /** 액션 버튼 숨김 여부 (기본: false) - 외부에서 버튼을 제공할 때 사용 */
  hideActionButtons?: boolean;
}

/**
 * 손실 책임 추가/편집 폼 컴포넌트
 */
const LiabilityForm: FC<LiabilityFormProps> = ({
  form,
  responsibleType,
  onSave,
  onCancel,
  isEditing,
  editingLiability,
  products,
  onProductSelect,
  showHeader = true,
  hideActionButtons = false,
}) => {
  const [amountInputMode, setAmountInputMode] = useState<AmountInputMode>('detail');
  const [totalInputAmount, setTotalInputAmount] = useState<number>(0);
  const [isTaxFree, setIsTaxFree] = useState(false);

  const supplyAmount = Form.useWatch('supplyAmount', form) ?? 0;
  const taxAmount = Form.useWatch('taxAmount', form) ?? 0;
  const totalAmount = supplyAmount + taxAmount;

  const hasProducts = products && products.length > 0;

  // 총액 입력값 변경 핸들러 (사용자가 직접 입력할 때)
  const handleTotalInputChange = (value: number | null) => {
    const newTotal = value ?? 0;
    setTotalInputAmount(newTotal);
    // 총액 모드에서 사용자 입력 시 공급가/세금 자동 계산
    const { price, tax } = calculatePreTaxPrice(newTotal, isTaxFree);
    form.setFieldsValue({
      supplyAmount: price,
      taxAmount: tax,
    });
  };

  // 세금 유무 변경 핸들러
  const handleTaxFreeChange = (taxFree: boolean) => {
    setIsTaxFree(taxFree);
    // 세금 유무 변경 시 공급가/세금 재계산
    const { price, tax } = calculatePreTaxPrice(totalInputAmount, taxFree);
    form.setFieldsValue({
      supplyAmount: price,
      taxAmount: tax,
    });
  };

  // 상품 선택 시 총액 모드일 경우 totalInputAmount, isTaxFree도 업데이트
  const handleProductSelectWithSync = (product: IssueProduct) => {
    onProductSelect?.(product);
    // 상품의 금액으로 totalInputAmount 동기화
    if (product.amount) {
      const productTotal = product.amount.supplyAmount + product.amount.taxAmount;
      setTotalInputAmount(productTotal);
      // 세금이 0이면 면세로 설정
      setIsTaxFree(product.amount.taxAmount === 0);
    }
  };

  // 모드 전환 시 총액 입력값 동기화
  const handleModeChange = (mode: AmountInputMode) => {
    if (mode === 'total') {
      // detail → total: 현재 총액을 totalInputAmount에 설정
      setTotalInputAmount(supplyAmount + taxAmount);
    }
    setAmountInputMode(mode);
  };

  const renderActionButtons = () => {
    if (hideActionButtons) return null;
    return (
      <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, ...labelAlignStyle }}>
        <Button type='primary' onClick={onSave}>
          {isEditing ? '수정' : '추가'}
        </Button>
        <Button onClick={onCancel}>취소</Button>
      </div>
    );
  };

  const separatorStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    height: 32,
    color: '#8c8c8c',
    fontSize: 16,
    ...labelAlignStyle,
  };

  const formContainerStyle: React.CSSProperties = showHeader
    ? {
        padding: 16,
        borderRadius: 8,
        marginBottom: 16,
        background: isEditing ? '#fff7e6' : '#f0f5ff',
        border: isEditing ? '1px solid #ffd591' : '1px solid #adc6ff',
      }
    : {};

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 16,
    paddingBottom: 12,
    borderBottom: isEditing ? '1px solid #ffd591' : '1px solid #adc6ff',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: 14,
    fontWeight: 600,
    color: isEditing ? '#d46b08' : '#1d39c4',
  };

  return (
    <div style={formContainerStyle}>
      {/* 폼 헤더 - 추가/편집 구분 */}
      {showHeader && (
        <div style={headerStyle}>
          {isEditing ? (
            <>
              <FormOutlined style={{ color: '#d46b08', fontSize: 16 }} />
              <span style={titleStyle}>손실 책임 편집</span>
              {editingLiability && (
                <Tag color='orange' style={{ marginLeft: 8 }}>
                  {issueLiabilityResponsibleTypeMap[editingLiability.responsibleType]} /{' '}
                  {formatAmount(editingLiability.amount.totalAmount)}
                </Tag>
              )}
            </>
          ) : (
            <>
              <PlusOutlined style={{ color: '#1d39c4', fontSize: 16 }} />
              <span style={titleStyle}>새 손실 책임 추가</span>
            </>
          )}
        </div>
      )}

      <Form form={form} layout='vertical'>
        {/* 1행: 책임자 유형 | 책임자 | 메모 */}
        <Space size='middle' style={{ display: 'flex', marginBottom: 0, minHeight: 96 }} align='start'>
          <Form.Item
            name='responsibleType'
            label='책임자 유형'
            rules={[{ required: true, message: '필수 항목입니다' }]}
            style={formItemStyle}
          >
            <Select
              options={responsibleTypeOptions}
              style={{ width: 120 }}
              onChange={() => form.setFieldValue('responsibleId', null)}
            />
          </Form.Item>
          <Form.Item
            name='responsibleId'
            label='책임자'
            rules={[{ required: true, message: '필수 항목입니다' }]}
            style={formItemStyle}
          >
            <ResponsibleIdSelector responsibleType={responsibleType} />
          </Form.Item>
          <Form.Item name='status' label='상태' rules={[{ required: true, message: '필수' }]} style={formItemStyle}>
            <Select options={statusOptions} style={{ width: 100 }} />
          </Form.Item>
          <Form.Item name='memo' label='메모' style={formItemStyle}>
            <Input placeholder='선택사항' style={{ width: 160 }} />
          </Form.Item>
          <Form.Item name='progressNote' label='진행 노트' style={{ ...formItemStyle, flex: 1 }}>
            <Input placeholder='처리 진행 상황 (선택사항)' style={{ width: 200 }} />
          </Form.Item>
        </Space>

        {/* 금액 입력 모드 선택 */}
        <div style={{ marginBottom: 12 }}>
          <Radio.Group
            value={amountInputMode}
            onChange={(e) => handleModeChange(e.target.value)}
            size='small'
            optionType='button'
            buttonStyle='solid'
          >
            <Radio.Button value='detail'>공급가/세금 입력</Radio.Button>
            <Radio.Button value='total'>총액 입력</Radio.Button>
          </Radio.Group>
        </div>

        {/* 2행: 금액 입력 영역 */}
        {amountInputMode === 'detail' ? (
          <Space size='middle' style={{ display: 'flex', minHeight: 96 }} align='start'>
            <Form.Item
              name='costType'
              label='비용 유형'
              rules={[{ required: true, message: '필수' }]}
              style={formItemStyle}
            >
              <Select options={costTypeOptions} style={{ width: 100 }} />
            </Form.Item>
            <Form.Item
              name='supplyAmount'
              label='공급가'
              rules={[{ required: true, message: '필수' }]}
              style={formItemStyle}
            >
              <InputNumber {...numberInputProps} style={{ width: 120 }} />
            </Form.Item>
            <Form.Item
              name='taxAmount'
              label='세금'
              rules={[{ required: true, message: '필수' }]}
              style={formItemStyle}
            >
              <InputNumber {...numberInputProps} style={{ width: 100 }} />
            </Form.Item>
            <div style={separatorStyle}>=</div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={labelStyle}>총금액</div>
              <div style={{ fontWeight: 600, fontSize: 16, height: 32, display: 'flex', alignItems: 'center' }}>
                {formatAmount(totalAmount)}
              </div>
            </div>
            {renderActionButtons()}
          </Space>
        ) : (
          <Space size='middle' style={{ display: 'flex' }} align='start'>
            <Form.Item
              name='costType'
              label='비용 유형'
              rules={[{ required: true, message: '필수' }]}
              style={formItemStyle}
            >
              <Select options={costTypeOptions} style={{ width: 100 }} />
            </Form.Item>
            <Form.Item label='총액' style={formItemStyle} required>
              <InputNumber
                value={totalInputAmount}
                onChange={handleTotalInputChange}
                {...numberInputProps}
                style={{ width: 120 }}
              />
            </Form.Item>
            <Form.Item label='세금' style={formItemStyle}>
              <Radio.Group value={isTaxFree} onChange={(e) => handleTaxFreeChange(e.target.value)} optionType='button'>
                <Radio.Button value={false}>과세</Radio.Button>
                <Radio.Button value={true}>면세</Radio.Button>
              </Radio.Group>
            </Form.Item>
            <div style={separatorStyle}>→</div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={labelStyle}>공급가</div>
              <div style={{ height: 32, display: 'flex', alignItems: 'center' }}>{formatAmount(supplyAmount)}</div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={labelStyle}>세금</div>
              <div style={{ height: 32, display: 'flex', alignItems: 'center' }}>{formatAmount(taxAmount)}</div>
            </div>
            {renderActionButtons()}
          </Space>
        )}

        {/* Hidden form fields for total mode (to pass validation) */}
        {amountInputMode === 'total' && (
          <>
            <Form.Item name='supplyAmount' hidden rules={[{ required: true }]}>
              <InputNumber />
            </Form.Item>
            <Form.Item name='taxAmount' hidden rules={[{ required: true }]}>
              <InputNumber />
            </Form.Item>
          </>
        )}

        {/* 상품으로 빠른 입력 영역 */}
        {hasProducts && !isEditing && (
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px dashed #d9d9d9' }}>
            <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 8 }}>상품으로 빠른 입력</div>
            <Space size={[8, 8]} wrap>
              {products.map((product, index) => (
                <Button
                  key={`${product.productId}-${index}`}
                  size='small'
                  onClick={() => handleProductSelectWithSync(product)}
                >
                  {product.productName} ({product.quantity}
                  {product.inventoryUnit})
                </Button>
              ))}
            </Space>
          </div>
        )}
      </Form>
    </div>
  );
};

export default LiabilityForm;
